import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../app/store";

export type BillStatus = "pending" | "paid" | "cancelled";

export type BillServiceType =
  | "new_offer"
  | "highlighted"
  | "recommended"
  | "extend_period";

export interface BillOffer {
  _id: string;
  title?: string;
  companyName?: string;
  name?: string;
}

export interface BillType {
  _id: string;
  user: string;

  // Lista użytkownika może zwracać samo ID,
  // a szczegóły z populate mogą zwrócić obiekt.
  offer: string | BillOffer;

  type: BillServiceType;
  title: string;
  amount: number;
  grossAmount: number;
  status: BillStatus;
  transferTitle: string;
  createdAt: string;
  paidAt?: string;
}

interface PayBillResponse {
  message?: string;
  bill: BillType;
}

interface BillState {
  bills: BillType[];

  selectedBill: BillType | null;

  loading: boolean;
  error: string;

  billLoading: boolean;
  billError: string;
}

const initialState: BillState = {
  bills: [],

  selectedBill: null,

  loading: false,
  error: "",

  billLoading: false,
  billError: "",
};

const getAuthConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": token || "",
    },
  };
};

/**
 * Lista rachunków zalogowanego użytkownika
 * GET /api/bills/my
 */
export const getMyBills = createAsyncThunk<
  BillType[],
  void,
  { rejectValue: string }
>(
  "bills/getMyBills",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        "/api/bills/my",
        getAuthConfig()
      );

      return res.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.msg ||
          error.response?.data?.error ||
          "Nie udało się pobrać rachunków"
      );
    }
  }
);

/**
 * Jeden rachunek zalogowanego użytkownika
 * GET /api/bills/my/:id
 */
export const getMyBill = createAsyncThunk<
  BillType,
  string,
  { rejectValue: string }
>(
  "bills/getMyBill",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `/api/bills/my/${id}`,
        getAuthConfig()
      );

      return res.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.msg ||
          error.response?.data?.error ||
          "Nie udało się pobrać rachunku"
      );
    }
  }
);

/**
 * Lista wszystkich rachunków dla administratora
 * GET /api/bills
 */
export const getBills = createAsyncThunk<
  BillType[],
  void,
  { rejectValue: string }
>(
  "bills/getBills",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        "/api/bills",
        getAuthConfig()
      );

      return res.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.msg ||
          error.response?.data?.error ||
          "Nie udało się pobrać wszystkich rachunków"
      );
    }
  }
);

/**
 * Jeden rachunek dla administratora
 * GET /api/bills/:id
 */
export const getBill = createAsyncThunk<
  BillType,
  string,
  { rejectValue: string }
>(
  "bills/getBill",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `/api/bills/${id}`,
        getAuthConfig()
      );

      return res.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.msg ||
          error.response?.data?.error ||
          "Nie udało się pobrać rachunku"
      );
    }
  }
);

/**
 * Oznaczenie rachunku jako opłacony
 * PATCH /api/bills/:id/pay
 */
export const payBill = createAsyncThunk<
  PayBillResponse,
  string,
  { rejectValue: string }
>(
  "bills/payBill",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.patch<PayBillResponse>(
        `/api/bills/${id}/pay`,
        {},
        getAuthConfig()
      );

      return res.data;
    } catch (err) {
      const error = err as any;

      return rejectWithValue(
        error.response?.data?.msg ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Nie udało się opłacić rachunku"
      );
    }
  }
);

const billSlice = createSlice({
  name: "bills",
  initialState,

  reducers: {
    clearSelectedBill: (state) => {
      state.selectedBill = null;
      state.billError = "";
      state.billLoading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // Rachunki zalogowanego użytkownika
      .addCase(getMyBills.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(
        getMyBills.fulfilled,
        (state, action: PayloadAction<BillType[]>) => {
          state.loading = false;
          state.bills = action.payload;
        }
      )
      .addCase(getMyBills.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          "Błąd pobierania rachunków";
      })

      // Jeden rachunek zalogowanego użytkownika
      .addCase(getMyBill.pending, (state) => {
        state.billLoading = true;
        state.billError = "";
        state.selectedBill = null;
      })
      .addCase(
        getMyBill.fulfilled,
        (state, action: PayloadAction<BillType>) => {
          state.billLoading = false;
          state.selectedBill = action.payload;
        }
      )
      .addCase(getMyBill.rejected, (state, action) => {
        state.billLoading = false;
        state.billError =
          action.payload ||
          "Błąd pobierania rachunku";
      })

      // Wszystkie rachunki dla administratora
      .addCase(getBills.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(
        getBills.fulfilled,
        (state, action: PayloadAction<BillType[]>) => {
          state.loading = false;
          state.bills = action.payload;
        }
      )
      .addCase(getBills.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          "Błąd pobierania wszystkich rachunków";
      })

      // Jeden rachunek dla administratora
      .addCase(getBill.pending, (state) => {
        state.billLoading = true;
        state.billError = "";
        state.selectedBill = null;
      })
      .addCase(
        getBill.fulfilled,
        (state, action: PayloadAction<BillType>) => {
          state.billLoading = false;
          state.selectedBill = action.payload;
        }
      )
      .addCase(getBill.rejected, (state, action) => {
        state.billLoading = false;
        state.billError =
          action.payload ||
          "Błąd pobierania rachunku";
      })

      // Oznaczenie rachunku jako opłacony
      .addCase(payBill.pending, (state) => {
        state.billLoading = true;
        state.billError = "";
      })
      .addCase(payBill.fulfilled, (state, action) => {
        state.billLoading = false;
        state.selectedBill = action.payload.bill;

        const billIndex = state.bills.findIndex(
          (bill) => bill._id === action.payload.bill._id
        );

        if (billIndex !== -1) {
          state.bills[billIndex] = action.payload.bill;
        }
      })
      .addCase(payBill.rejected, (state, action) => {
        state.billLoading = false;
        state.billError =
          action.payload ||
          "Nie udało się opłacić rachunku";
      });
  },
});

export const { clearSelectedBill } = billSlice.actions;

export const selectBills = (state: RootState) =>
  state.bills.bills;

export const selectBillsLoading = (state: RootState) =>
  state.bills.loading;

export const selectBillsError = (state: RootState) =>
  state.bills.error;

export const selectBill = (state: RootState) =>
  state.bills.selectedBill;

export const selectBillLoading = (state: RootState) =>
  state.bills.billLoading;

export const selectBillError = (state: RootState) =>
  state.bills.billError;

export default billSlice.reducer;